import { useEffect, useRef, useState } from "react";
import { Globe } from "lucide-react";
import { SunLogo } from "../components/SunLogo";
import { MENU, tr, trName } from "../data/menu";
import { INFO } from "../data/info";
import { useLang, LANGS } from "../i18n";

// Page /carta : la carte seule, pensée pour le téléphone.
// C'est l'adresse du QR code posé sur les tables.

const TXT = {
  lang: { es: "Idioma", fr: "Langue", ar: "اللغة", ru: "Язык" },
  site: { es: "Ver la web", fr: "Voir le site", ar: "زيارة الموقع", ru: "Открыть сайт" },
};

const Row = ({ n, p, d, lang }) => (
  <li className="border-b border-[#F5EDE3]/10 py-3.5">
    <div className="flex items-baseline gap-3">
      <span className="font-sans text-[15px] text-[#F5EDE3]/90">{trName(n, lang)}</span>
      <span className="mb-1 flex-1 border-b border-dotted border-[#F5EDE3]/20" />
      <span className="font-sans text-[15px] tabular-nums text-[#D9B98A]">{p} €</span>
    </div>
    {d && <p className="mt-1 font-sans text-[13px] leading-snug text-[#F5EDE3]/45">{tr(d, lang)}</p>}
  </li>
);

const GroupTitle = ({ children }) => (
  <h3 className="mb-3 mt-8 font-sans text-[11px] uppercase tracking-[0.26em] text-[#D9B98A]">{children}</h3>
);

const Category = ({ cat, lang }) => (
  <section id={`m-${cat.id}`} className="scroll-mt-[136px] pt-12">
    <h2 className="font-serif text-[34px] font-light leading-none text-[#F5EDE3]">{tr(cat.label, lang)}</h2>
    {cat.note && (
      <p className="mt-3 font-sans text-[11px] uppercase tracking-[0.24em] text-[#C4886B]">{tr(cat.note, lang)}</p>
    )}

    {cat.photos && (
      <div className="-mx-5 mt-6 flex gap-2 overflow-x-auto px-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {cat.photos.map((ph) => (
          <img key={ph.src} src={ph.src} alt={ph.alt} loading="lazy" className="aspect-[4/5] w-[44%] shrink-0 rounded-xl object-cover" />
        ))}
      </div>
    )}

    {cat.items && (
      <ul className="mt-4">
        {cat.items.map((it) => (
          <Row key={it.n} lang={lang} {...it} />
        ))}
      </ul>
    )}

    {cat.groups &&
      cat.groups.map((g) => (
        <div key={g.key}>
          <GroupTitle>{tr(g.title, lang)}</GroupTitle>
          {g.items && (
            <ul className="border-t border-[#F5EDE3]/10">
              {g.items.map((it) => (
                <Row key={it.n} lang={lang} {...it} />
              ))}
            </ul>
          )}
          {g.flat && (
            <ul className="flex flex-wrap gap-2">
              {tr(g.flat, lang).map((f) => (
                <li key={f} className="rounded-full border border-[#F5EDE3]/15 px-3 py-1.5 font-sans text-[13px] text-[#F5EDE3]/75">
                  {f}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}

    {cat.steps && (
      <ol className="mt-6 space-y-5">
        {cat.steps.map((s) => (
          <li key={s.k} className="flex gap-4 border-t border-[#D9B98A]/25 pt-4">
            <span className="font-serif text-3xl leading-none text-[#D9B98A]/60">{s.k}</span>
            <div>
              <p className="font-sans text-[11px] uppercase tracking-[0.24em] text-[#D9B98A]">{tr(s.t, lang)}</p>
              <p className="mt-1.5 font-sans text-[14px] leading-relaxed text-[#F5EDE3]/70">{tr(s.v, lang)}</p>
            </div>
          </li>
        ))}
      </ol>
    )}

    {cat.foot && <p className="mt-6 font-sans text-[13px] leading-snug text-[#F5EDE3]/45">{tr(cat.foot, lang)}</p>}
  </section>
);

export const MenuPage = () => {
  const { lang, setLang, t } = useLang();
  const [active, setActive] = useState(MENU[0].id);
  const chipsRef = useRef(null);

  useEffect(() => {
    document.title = `${t.carta.label} · Deseos Brunch & Coffee`;
  }, [t]);

  // Surligne la catégorie visible pendant le défilement.
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id.slice(2));
        });
      },
      { rootMargin: "-140px 0px -65% 0px" }
    );
    MENU.forEach((c) => {
      const el = document.getElementById(`m-${c.id}`);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [lang]);

  // Garde la pastille active visible dans la barre.
  useEffect(() => {
    const chip = chipsRef.current?.querySelector(`[data-cat="${active}"]`);
    chip?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [active]);

  const go = (id) => document.getElementById(`m-${id}`)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-[#2B1E16] text-[#F5EDE3] antialiased">
      <header className="sticky top-0 z-20 bg-[#2B1E16]/95 backdrop-blur">
        <div className="mx-auto flex max-w-[640px] items-center justify-between px-5 pt-4">
          <a href="/" className="flex items-center gap-2.5" aria-label="Deseos">
            <SunLogo animate={false} className="h-6 w-auto" />
            <span className="font-sans text-[12px] uppercase tracking-[0.34em] text-[#F5EDE3]">Deseos</span>
          </a>

          <div role="group" aria-label={tr(TXT.lang, lang)} className="flex items-center gap-1.5">
            <Globe className="me-0.5 h-4 w-4 text-[#D9B98A]/70" aria-hidden="true" />
            {LANGS.map((l) => (
              <button
                key={l.code}
                type="button"
                data-testid={`menu-lang-${l.code}`}
                onClick={() => setLang(l.code)}
                aria-label={l.name}
                aria-pressed={lang === l.code}
                className={`h-8 min-w-[34px] rounded-full px-2 font-sans text-[11px] font-medium tracking-[0.1em] transition-colors ${
                  lang === l.code
                    ? "bg-[#D9B98A] text-[#2B1E16]"
                    : "border border-[#F5EDE3]/20 text-[#F5EDE3]/70"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        <nav
          ref={chipsRef}
          aria-label={t.carta.label}
          className="mx-auto mt-4 flex max-w-[640px] gap-2 overflow-x-auto px-5 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {MENU.map((c) => (
            <button
              key={c.id}
              data-cat={c.id}
              type="button"
              onClick={() => go(c.id)}
              className={`shrink-0 rounded-full px-3.5 py-2 font-sans text-[12px] transition-colors ${
                active === c.id ? "bg-[#F5EDE3] text-[#2B1E16]" : "bg-[#F5EDE3]/[0.07] text-[#F5EDE3]/70"
              }`}
            >
              {tr(c.label, lang)}
            </button>
          ))}
        </nav>
        <div className="h-px w-full bg-[#D9B98A]/20" />
      </header>

      <main className="mx-auto max-w-[640px] px-5 pb-16">
        <div className="pt-10">
          <p className="font-sans text-[11px] uppercase tracking-[0.3em] text-[#D9B98A]">{t.carta.label}</p>
          <h1 className="mt-3 font-serif text-[40px] font-light leading-[1.05]">{t.carta.title}</h1>
        </div>

        {MENU.map((c) => (
          <Category key={c.id} cat={c} lang={lang} />
        ))}

        <footer className="mt-16 border-t border-[#D9B98A]/20 pt-8 text-center">
          <SunLogo animate={false} className="mx-auto h-8 w-auto" />
          <p className="mt-4 font-sans text-[13px] text-[#F5EDE3]/60">{t.footer.hours}</p>
          <p className="mt-1 font-sans text-[13px] text-[#F5EDE3]/60">
            {INFO.address} · {INFO.city}
          </p>
          <a href="/" className="btn-gold mt-7 inline-block">
            {tr(TXT.site, lang)}
          </a>
        </footer>
      </main>
    </div>
  );
};
